import { POST } from 'common/helpers.ts';
import useSWR from 'swr';

type ResourcePermissionRequest = {
  resourceId: string;
  operations: string[];
};

type PermissionCheck = {
  operation: string;
  granted: boolean;
};

type ResourcePermissionResult = {
  resourceId: string;
  permissions: PermissionCheck[];
};

type BatchResourcePermissionsResponse = {
  results: ResourcePermissionResult[];
};

export const useBatchResourcePermissions = (resources: ResourcePermissionRequest[]) => {
  const shouldFetch = resources.length > 0 && resources.every((r) => r.resourceId && r.operations.length > 0);

  const { data, error, isLoading } = useSWR<BatchResourcePermissionsResponse>(shouldFetch ? [`/permission/check-batch`, { resources }] : null, POST, {
    dedupingInterval: 30_000, // Cache for 30 seconds
    revalidateOnFocus: false,
  });

  const getPermission = (resourceId: string, operation: string): boolean | undefined => {
    if (!data) return undefined;
    const resourceResult = data.results.find((r) => r.resourceId === resourceId);
    if (!resourceResult) return undefined;
    const permission = resourceResult.permissions.find((p) => p.operation === operation);
    return permission?.granted;
  };

  const getResourcePermissions = (resourceId: string): PermissionCheck[] => {
    if (!data) return [];
    const resourceResult = data.results.find((r) => r.resourceId === resourceId);
    return resourceResult?.permissions || [];
  };

  return { results: data?.results || [], getPermission, getResourcePermissions, isLoading, error };
};
